import {
  GridActionsCellItem,
  GridAlignment,
  GridColDef,
  GridColType,
  GridInitialState,
  GridRenderCellParams,
  GridRowParams,
  GridRowSpacingParams,
  GridSelectionModel,
  GridSortDirection,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef
} from '@mui/x-data-grid-pro';

import * as React from 'react';
import { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Button,
  Checkbox,
  Confirm,
  DataGridApi,
  DataGridCallbacks,
  EIcons,
  Icon,
  Input,
  Menu,
  Stack,
  TIcons
} from 'ui';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { debounce, debounceAsync } from 'utils/debounce';
import LinearProgress from '@mui/material/LinearProgress';
import { IButtonProps } from '../Button';
import { DataGridView } from './components/DataGridView';
import { RowActionButtons, RowActionType } from './DataGrid.types';
import { styled, SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { DataGridPropsWithoutDefaultValue } from '@mui/x-data-grid/models/props/DataGridProps';
import { GridCellParams } from '@mui/x-data-grid/models/params/gridCellParams';

export * from './components/DataGridView';
export * from './DataGrid.types';

export * from './hooks';

export type { GridValidRowModel, GridSortDirection };

export type GlobalActionType =
  | 'create'
  | 'deleteMultiple'
  | 'editMultiple'
  | 'delete'
  | 'edit'
  | 'view'
  | 'customAction';

export interface RefreshTrigger {
  counter: number;
  page: 'SamePage' | 'FirstPage';
}

export interface GlobalActionButtons {
  label: string;
  icon?: TIcons;
  color?: IButtonProps['color'];
  onClick?: (selectedIds: any[], callbacks?: DataGridCallbacks) => Promise<void> | void;
  type?: GlobalActionType;
  dialogType?: string;
  enableWhenNoSelection?: boolean;
  showInMenu?: boolean;
  disableByKey?: string;
  isDisabled?: (row: GridValidRowModel, index: number, selectedRows: GridValidRowModel[]) => boolean;
  extraAttributes?: Record<string, string>;
}
export interface ColumnDefinition extends GridColDef {
  field: string;
  headerName: string;
  type: GridColType;
  renderCell?: (params: GridRenderCellParams) => React.ReactNode;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  searchable?: boolean;
  sortable?: boolean;
  valueFormatter?: any;
}

interface InnerColumnDefinition extends ColumnDefinition {
  flex?: number;
}

export interface DataResponse {
  rows: GridValidRowModel[];
  totalRows: number;
  error?: boolean;
  message?: string;
}

export interface DeleteEntityDialogArguments {
  refreshPage: () => Promise<void>;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  selectedEntityData: GridValidRowModel | undefined;
  entities: GridValidRowModel[];
  isLoading?: boolean;
}

export interface EditEntityDialogArguments {
  refreshPage: () => Promise<void>;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  selectedEntityData: GridValidRowModel | undefined;
  entities: GridValidRowModel[];
  dialogType: 'single' | 'multiple';
}

interface Props {
  fetchData: (
    page: number,
    pageNumber: number,
    orderBy: string,
    descending: boolean,
    search: string
  ) => Promise<DataResponse>;
  actionsButtons?: GlobalActionButtons[];
  rowUniqueId: string;
  columnsDefinition: ColumnDefinition[];
  rowActionButtons?: RowActionButtons[];
  entity: string;
  CreateEntityForm?: React.ComponentType<{
    refreshPage: () => Promise<any>;
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    title?: string;
  }>;
  renderDeleteEntityDialog?: (args: DeleteEntityDialogArguments) => void;
  renderCustomActionDialog?: (args: any) => void;
  renderEditEntityDialog?: (args: EditEntityDialogArguments) => void;
  createEntity?: (entity: any) => Promise<boolean>;
  createEntityFormProps?: any;
  deleteEntity?: (entity: any, selectedEntity?: any) => Promise<boolean>;
  deleteEntities?: (ids: any[], selectedRows?: any) => Promise<boolean>;
  refreshTrigger?: RefreshTrigger;
  autoHeight?: boolean;
  autoPageSize?: boolean;
  pageSize?: number;
  checkboxSelection?: boolean;
  hideSearch?: boolean;
  sx?: SxProps<Theme>;
  searchInputSx?: SxProps<Theme>;
  initialState?: GridInitialState;
  disableSelectionOnClick?: boolean;
  NoResultsOverlay?: React.JSXElementConstructor<any>;
  NoRowsOverlay?: React.JSXElementConstructor<any>;
  getRowHeight?: DataGridPropsWithoutDefaultValue['getRowHeight'];
  disableMultipleSelection?: boolean;
  searchPlaceholder?: string;
  getDetailPanelContent?: (params: GridRowParams) => React.ReactNode;
  getDetailPanelHeight?: (params: GridRowParams) => number | 'auto';
  getCellClassName?: (params: GridCellParams) => string;
}

const FIRST_PAGE = 1;
const PAGE_SIZE = 100;

const GlobalActionButton = styled(Button)(({ theme }) => ({
  '&.Mui-disabled svg path': {
    fill: theme.palette.grey[300]
  }
}));

const DataGrid = forwardRef<DataGridApi | undefined, Props>(
  (
    {
      actionsButtons,
      fetchData,
      rowUniqueId,
      columnsDefinition,
      rowActionButtons,
      entity,
      CreateEntityForm,
      createEntityFormProps = {},
      renderDeleteEntityDialog,
      renderEditEntityDialog,
      renderCustomActionDialog,
      deleteEntity,
      deleteEntities,
      refreshTrigger,
      autoHeight,
      autoPageSize,
      pageSize = PAGE_SIZE,
      checkboxSelection = true,
      hideSearch,
      sx,
      initialState,
      disableSelectionOnClick = true,
      NoResultsOverlay,
      NoRowsOverlay,
      getRowHeight,
      disableMultipleSelection,
      searchPlaceholder = 'Search',
      searchInputSx,
      getDetailPanelContent,
      getDetailPanelHeight,
      getCellClassName
    }: Props,
    ref
  ) => {
    const apiRef = useGridApiRef();
    const getRowSpacing = useCallback((params: GridRowSpacingParams) => {
      return {
        top: params.isFirstVisible ? 6 : 0,
        bottom: params.isLastVisible ? 0 : 6
      };
    }, []);
    const initialSortModel = initialState?.sorting?.sortModel;
    const [sortField, setSortField] = useState<string | undefined>(initialSortModel && initialSortModel[0].field);
    const [sortDirection, setSortDirection] = useState<GridSortDirection>(initialSortModel && initialSortModel[0].sort);
    const [searchString, setSearchString] = useState('');
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
    const [selectedRows, setSelectedRows] = useState<GridValidRowModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [confirmType, setConfirmType] = useState<null | RowActionType | GlobalActionType>(null);
    const [dialogType, setDialogType] = useState<null | string>(null);
    const [confirmRowId, setConfirmRowId] = useState<any>(null);
    const [page, setPage] = useState(FIRST_PAGE);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState<GridValidRowModel[]>([]);
    const [error, setError] = useState(false);

    const callbacks = {
      refreshPage: () => loadPage(FIRST_PAGE),
      setLoading: (isLoading: boolean) => setLoading(isLoading)
    };

    useImperativeHandle(ref, () => ({ ...callbacks }), [callbacks]);
    const handleRowActionClick = useCallback(
      async (params: any, action: RowActionButtons) => {
        if (action.type) {
          setConfirmType(action.type);
          setConfirmRowId(params?.id);
          setSelectedRows(rows.filter(row => row[rowUniqueId] === params?.id));
          if (disableMultipleSelection) {
            setSelectionModel([params?.id]);
          } else {
            setSelectionModel(prevSelectionModel => [...prevSelectionModel, params?.id]);
          }

          if (action.dialogType) {
            setDialogType(action.dialogType);
          }
        } else {
          action.onClick?.(params?.row, callbacks);
        }
      },
      [rows, selectedRows]
    );

    const handleGlobalActionClick = useCallback(
      async (action: GlobalActionButtons) => {
        if (action.type) {
          setConfirmType(action.type);
          if (!disableSelectionOnClick && selectedRows.length > 0) {
            setConfirmRowId(selectedRows[0][rowUniqueId]);
          }
          if (action.dialogType) {
            setDialogType(action.dialogType);
          }
        } else {
          action.onClick && action.onClick(selectedRows, callbacks);
        }
      },
      [rows, selectedRows]
    );

    const onSortChange = useCallback(([sortModel]: GridSortModel) => {
      if (!sortModel) return;

      const { field, sort } = sortModel;

      if (!field || !sort) return;

      setSortField(field);
      setSortDirection(sort);
    }, []);

    const onSearchStringChange = debounce(async (event: ChangeEvent<HTMLInputElement>) => {
      setSearchString(event.target.value || '');
    }, 500);

    const updateRows = useCallback((response: DataResponse, page: number) => {
      setRows(response.rows || []);
      setRowCount(response.totalRows || 0);
      setPage(page);
    }, []);

    const loadPage = useCallback(
      async page => {
        const pageSize = apiRef.current.state?.pagination?.pageSize;
        if (!pageSize) {
          return;
        }
        setLoading(true);
        setError(false);
        const response = await debouncedFetchData(
          page,
          pageSize,
          sortField || '',
          sortDirection === 'desc',
          searchString
        );
        if (!response || response.error) {
          setError(true);
        } else {
          updateRows(response, page);
        }
        setLoading(false);
      },
      [fetchData, searchString, sortDirection, sortField, updateRows]
    );

    const debouncedFetchData = useCallback(debounceAsync(fetchData, 500), [fetchData]);

    const onPageChange = useCallback(
      async nextPage => {
        if (nextPage !== page) {
          void loadPage(nextPage);
        }
      },
      [loadPage, page]
    );

    const onPageSizeChange = useCallback(
      async newPageSize => {
        void loadPage(page);
      },
      [loadPage, page]
    );

    useEffect(() => {
      setSelectionModel([]);
      setSelectedRows([]);
      const moveToPage = refreshTrigger?.page === 'SamePage' ? page : FIRST_PAGE;
      void loadPage(moveToPage);
    }, [searchString, sortField, sortDirection, loadPage, refreshTrigger]);

    const columnMapping = columnsDefinition.map(col => ({
      flex: col.flex ?? 1,
      headerAlign: 'left' as GridAlignment,
      align: 'left' as GridAlignment,
      ...col
    })) as InnerColumnDefinition[];

    const hasRowActions = rowActionButtons && rowActionButtons.length > 0;
    if (hasRowActions) {
      let displayedButtons = 0;
      let inMenuButtons = 0;

      rowActionButtons.forEach(action => (action.showInMenu ? inMenuButtons++ : displayedButtons++));
      const numOfMaxDisplayedActionIcons = Math.min(1, inMenuButtons) + displayedButtons;
      const iconWidth = 34;
      const iconsGap = 8;
      const columnPadding = 20;
      const gaps = numOfMaxDisplayedActionIcons - 1;
      const width = numOfMaxDisplayedActionIcons * iconWidth + iconsGap * gaps + columnPadding;
      const minWidth = 75; // minWidth is needed to prevent cutting the column header ("Actions")
      const rowActionsMapping = {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        minWidth: width > minWidth ? width : minWidth,
        maxWidth: width > minWidth ? width : minWidth,
        headerAlign: 'left' as GridAlignment,
        align: 'left' as GridAlignment,
        getActions: (params: GridRowParams) => {
          return rowActionButtons?.map(action => {
            let isDisabled = false;
            let isHidden = false;

            if (action.disableByKey) {
              isDisabled = params.row[action.disableByKey];
            } else if (action.isDisabled) {
              isDisabled = action.isDisabled(params.row, 0, selectedRows);
              isHidden = isDisabled;
            }

            const label = typeof action.label === 'string' ? action.label : action.label(params);

            const iconName = typeof action.icon === 'string' ? action.icon : action?.icon?.(params);

            return (
              <GridActionsCellItem
                key={label}
                disabled={isDisabled}
                icon={<Icon name={iconName as TIcons} />}
                label={label}
                onClick={() => handleRowActionClick(params, action)}
                showInMenu={action.showInMenu}
                nonce={undefined}
                sx={{ display: isHidden ? 'none' : 'inline-flex' }}
                {...action.getExtraAttributes?.(params)}
              />
            );
          });
        }
      };

      columnMapping.push(rowActionsMapping);
    }

    const handleDeleteAction = async (selectedRowId: string) => {
      const id = selectedRowId || confirmRowId;
      const selectedRow = selectedRows[0];

      if (deleteEntity) {
        await deleteEntity(id, selectedRow);

        setConfirmType(null);
        setConfirmRowId(null);
        setSelectionModel([]);
        setSelectedRows([]);
        void loadPage(page);
        setLoading(false);
      }
    };

    const handleDeleteMultipleAction = async () => {
      if (deleteEntities) {
        await deleteEntities(selectionModel, selectedRows);

        setConfirmType(null);
        setSelectionModel([]);
        setSelectedRows([]);
        setLoading(false);
        void loadPage(page);
      }
    };

    const renderActionDialog = (confirmType: string | null) => {
      switch (confirmType) {
        case 'delete':
          if (renderDeleteEntityDialog !== undefined) {
            return renderDeleteEntityDialog({
              refreshPage: () => loadPage(FIRST_PAGE),
              open: true,
              onCancel: () => setConfirmType(null),
              onConfirm: () => handleDeleteAction(confirmRowId),
              selectedEntityData: selectedRows[0],
              entities: rows
            });
          } else {
            return (
              <Confirm
                onCancel={() => setConfirmType(null)}
                onConfirm={() => {
                  setLoading(true);
                  void handleDeleteAction(confirmRowId);
                }}
                title={`Are you sure you want to\ndelete this ${entity}?`}
                open={true}
                isLoading={loading}
              />
            );
          }
          break;
        case 'deleteMultiple':
          if (renderDeleteEntityDialog !== undefined) {
            return renderDeleteEntityDialog({
              refreshPage: () => loadPage(FIRST_PAGE),
              open: true,
              onCancel: () => setConfirmType(null),
              onConfirm: () => {
                setLoading(true);
                void handleDeleteAction(selectedRows[0].guid);
              },
              selectedEntityData: selectedRows[0],
              entities: rows,
              isLoading: loading
            });
          } else {
            return (
              <Confirm
                onCancel={() => setConfirmType(null)}
                onConfirm={() => {
                  setLoading(true);
                  void handleDeleteMultipleAction();
                }}
                title={`Are you sure you want to delete\n${selectionModel.length} ${entity}${
                  selectionModel.length > 1 ? 's' : ''
                }?`}
                open={true}
                isLoading={loading}
              />
            );
          }
          break;
        case 'create':
          if (CreateEntityForm) {
            return (
              <CreateEntityForm
                refreshPage={() => loadPage(FIRST_PAGE)}
                open={true}
                onCancel={() => setConfirmType(null)}
                onConfirm={() => {
                  setConfirmType(null);
                }}
                {...createEntityFormProps}
              />
            );
          } else {
            return null;
          }
          break;
        case 'edit':
          if (renderEditEntityDialog) {
            return renderEditEntityDialog({
              refreshPage: () => loadPage(FIRST_PAGE),
              open: true,
              onCancel: () => setConfirmType(null),
              onConfirm: () => {
                setConfirmType(null);
                setConfirmRowId(null);
                setSelectionModel([]);
                void loadPage(page);
              },
              selectedEntityData: selectedRows[0],
              entities: rows,
              dialogType: 'single'
            });
          } else {
            return null;
          }
          break;
        case 'editMultiple':
          if (renderEditEntityDialog) {
            return renderEditEntityDialog({
              refreshPage: () => loadPage(FIRST_PAGE),
              open: true,
              onCancel: () => setConfirmType(null),
              onConfirm: () => {
                setConfirmType(null);
                setConfirmRowId(null);
                setSelectionModel([]);
                void loadPage(page);
              },
              selectedEntityData: selectedRows,
              entities: rows,
              dialogType: 'multiple'
            });
          }
          setConfirmType(null);
          return null;
          break;
        case 'customAction':
          if (renderCustomActionDialog) {
            return renderCustomActionDialog({
              refreshPage: () => loadPage(FIRST_PAGE),
              onCancel: () => setConfirmType(null),
              onConfirm: () => {
                setConfirmType(null);
                setConfirmRowId(null);
                setSelectionModel([]);
                void loadPage(page);
              },
              open: true,
              selectedEntityData: selectedRows.length > 1 ? selectedRows : selectedRows[0],
              entities: rows,
              dialogType
            });
          } else {
            return null;
          }
          setConfirmType(null);
          break;
        default:
          return null;
      }
    };

    const globalActionsButtons = actionsButtons ? actionsButtons.filter(action => !action.showInMenu) : [];
    const globalActionsButtonsInMenu = actionsButtons ? actionsButtons.filter(action => action.showInMenu) : [];

    const isGlobalActionDisabled = (action: GlobalActionButtons) => {
      if (action.enableWhenNoSelection) return false;

      if (selectedRows.length > 0) {
        if (action.isDisabled && selectedRows.some(action.isDisabled)) {
          return true;
        } else {
          return action.disableByKey && selectedRows[0][action.disableByKey];
        }
      }
      return true;
    };

    return (
      <>
        {renderActionDialog(confirmType)}
        <DataGridView
          apiRef={apiRef}
          header={
            <>
              {!error && !hideSearch && (
                <Input
                  type="text"
                  onChange={onSearchStringChange}
                  placeholder={searchPlaceholder}
                  startAdornment={<SearchOutlinedIcon />}
                  sx={searchInputSx}
                />
              )}
              {(globalActionsButtonsInMenu.length > 0 || globalActionsButtons.length > 0) && !error && (
                <Stack direction="row" marginLeft={'auto'} spacing={2}>
                  {globalActionsButtons.map(action => (
                    <GlobalActionButton
                      key={action.label}
                      data-testid={`grid-global-action-${action.label}`}
                      color={action.color ?? 'secondary'}
                      iconColor={isGlobalActionDisabled(action) ? 'primary' : undefined}
                      startIcon={action.icon}
                      startIconHeight={24}
                      startIconWidth={24}
                      disabled={isGlobalActionDisabled(action)}
                      onClick={() => handleGlobalActionClick(action)}
                      {...action.extraAttributes}
                    >
                      {action.label}
                    </GlobalActionButton>
                  ))}
                  {globalActionsButtonsInMenu.length > 0 && (
                    <Menu
                      items={globalActionsButtonsInMenu.map((action, index) => ({
                        value: index,
                        label: action.label,
                        disabled: isGlobalActionDisabled(action),
                        icon: action.icon
                      }))}
                      onItemClick={value => handleGlobalActionClick(globalActionsButtonsInMenu[value])}
                    />
                  )}
                </Stack>
              )}
            </>
          }
          checkboxSelection={checkboxSelection}
          onSelectionModelChange={ids => {
            setSelectionModel(ids);

            const pageRowsIds = rows.map(row => row[rowUniqueId]);
            const notInPageSelectedRows = selectedRows.filter(row => !pageRowsIds.includes(row[rowUniqueId]));
            const selectedRowsInPageById = rows.filter(row => ids.includes(row[rowUniqueId]));
            setSelectedRows([...notInPageSelectedRows, ...selectedRowsInPageById]);
          }}
          sortingMode="server"
          paginationMode="server"
          selectionModel={selectionModel}
          onSortModelChange={sortModel => onSortChange(sortModel)}
          disableSelectionOnClick={disableSelectionOnClick}
          disableColumnMenu
          keepNonExistentRowsSelected
          onPageChange={newPage => onPageChange(newPage + 1)}
          onPageSizeChange={newPageSize => onPageSizeChange(newPageSize)}
          rowCount={rowCount}
          rowsPerPageOptions={[PAGE_SIZE]}
          getRowSpacing={getRowSpacing}
          initialState={initialState}
          components={{
            BaseCheckbox: forwardRef((props, ref) => <Checkbox color="default" inputRef={ref} {...props} />),
            MoreActionsIcon: props => <Icon name={EIcons.moreActions} {...props} />,
            LoadingOverlay: () => <LinearProgress style={{ zIndex: 1 }} />,
            NoRowsOverlay: NoRowsOverlay ? NoRowsOverlay : undefined,
            NoResultsOverlay: NoResultsOverlay ? NoResultsOverlay : undefined
          }}
          getRowId={(row: any) => row[rowUniqueId]}
          page={page - 1}
          pagination
          loading={loading}
          columns={columnMapping}
          rows={rows}
          error={error || undefined}
          autoHeight={autoHeight !== false}
          autoPageSize={autoPageSize}
          pageSize={autoPageSize ? undefined : pageSize}
          sx={sx}
          getRowHeight={getRowHeight}
          disableMultipleSelection={disableMultipleSelection}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          getCellClassName={getCellClassName}
        />
      </>
    );
  }
);

export default DataGrid;

export * from './Cells';
export * from './hooks';
export * from './createGroupColumns';
